/**
 * `/auth/session-expired` — the global interstitial shown when a session expires
 * at runtime (a bearer-surface `401` that could not be silently refreshed). It
 * informs the operator, counts down 3 seconds, then redirects to centralized
 * login via the shared `redirectToCentralizedLogin` primitive — which stashes the
 * current deep link so the operator lands back where they were after re-auth.
 *
 * The runtime trigger is the in-place render from `AuthGate` (driven by the
 * `AuthContext.sessionEnd === 'expired'` flag set from `signOut({ reason: 'expired' })`); the
 * public route registration is only for addressability/testing parity with the
 * other `/auth/*` screens. Because it renders in place (no route change),
 * `useLocation()` still reports the screen the operator was on, so the stashed
 * deep link is correct.
 *
 * The countdown is not announced every second (TMM-B3, TMMRSL-0070): the live region holds the
 * title, the body and one visually hidden, static "Redirecting in 3…" with the starting value. The
 * ticking paragraph sits outside the region and is `aria-hidden` (visual only).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SkipLink } from '@/components/SkipLink';
import { visuallyHidden } from '@/components/visuallyHidden';
import { redirectToCentralizedLogin } from '@/auth/silentRefresh';

const COUNTDOWN_SECONDS = 3;

export function SessionExpired() {
  const { t } = useTranslation();
  const location = useLocation();
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Single-fire guard: the countdown reaching 0 and the "Sign in now" button must
  // never both redirect. The ref survives re-renders, so the first caller wins.
  const redirected = useRef(false);

  const goToLogin = useCallback(() => {
    if (redirected.current) return;
    redirected.current = true;
    redirectToCentralizedLogin(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  // Tick the countdown once per second; cleaned up on unmount.
  useEffect(() => {
    const id = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Redirect when the countdown reaches 0 (guarded against double-fire above).
  useEffect(() => {
    if (seconds === 0) goToLogin();
  }, [seconds, goToLogin]);

  return (
    <>
      <SkipLink />
      <main
        id="main"
        tabIndex={-1}
        style={{ padding: '2rem', maxWidth: '32rem', margin: '0 auto' }}
      >
        <div role="status" aria-live="polite">
          <h1 ref={headingRef} tabIndex={-1}>
            {t('auth.sessionExpiredTitle')}
          </h1>
          <p>{t('auth.sessionExpiredBody')}</p>
          <span style={visuallyHidden}>
            {t('auth.redirectingIn', { seconds: COUNTDOWN_SECONDS })}
          </span>
        </div>
        <p aria-hidden="true">{t('auth.redirectingIn', { seconds })}</p>
        <button type="button" onClick={goToLogin}>
          {t('auth.signInNow')}
        </button>
      </main>
    </>
  );
}
